import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    IconButton,
    LinearProgress,
    MenuItem,
    Modal,
    Paper,
    Slide,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Theme,
    Tooltip,
    Typography,
} from '@material-ui/core'
import { Close, InfoOutlined } from '@material-ui/icons'
import { DateTime } from 'luxon'
import { toast } from 'react-toastify'
import { useQueryClient } from '@tanstack/react-query'

import { Container } from '../../../../components'
import { axiosInstance } from '../../../../helpers'

export interface BulkCreateWorkspace {
    id: number
    name: string
}

interface CreatedItem {
    apartment_id: number
    apartment_name: string
    name?: string
    schedule_id?: number
    schedule_code?: string
}

interface SkippedItem {
    apartment_id: number
    apartment_name: string
    reason: string
}

interface ErrorItem {
    apartment_id: number
    apartment_name?: string
    error: string
}

interface StartTaskResponse {
    task_id: string
    total: number
    message: string
}

interface TaskStatusResponse {
    state: string
    current?: number
    total?: number
    apartment_name?: string | null
    created?: CreatedItem[]
    skipped?: SkippedItem[]
    errors?: ErrorItem[]
    result?: {
        created: CreatedItem[]
        skipped: SkippedItem[]
        errors: ErrorItem[]
    }
    error?: string
}

type ModalPhase = 'form' | 'running' | 'done' | 'failed'

interface Props {
    open: boolean
    theme: Theme
    workspaces: BulkCreateWorkspace[]
    onClose: () => void
    /** Prefill turn year when opened from Manage Turn Projects. */
    initialYear?: number
    /** Disable year changes (year controlled by parent panel). */
    lockYear?: boolean
    /** Hide property checklist — workspaces prop is the final selection. */
    hidePropertyPicker?: boolean
    /** @deprecated Prefer Manage Turn Projects schedule flow. */
    onScheduleCreated?: (year: number, scheduleIds: number[]) => void
}

const POLL_INTERVAL_MS = 1500

function currentTurnYear(): number {
    return DateTime.now().year
}

function buildScheduleId(year: number): string {
    return `BCTURN${String(year).slice(-2)}`
}

const BufferRow = (props: {
    theme: Theme
    title: string
    prefix: string
    value: number
    onChange: (n: number) => void
    suffix: string
    tooltip: string
    disabled?: boolean
}) => {
    const {
        theme,
        title,
        prefix,
        value,
        onChange,
        suffix,
        tooltip,
        disabled,
    } = props
    return (
        <Box style={{ marginTop: theme.spacing(2) }}>
            <Typography
                variant="subtitle1"
                style={{ fontWeight: 700, marginBottom: theme.spacing(0.5) }}
            >
                {title}
            </Typography>
            <Container
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: theme.spacing(1),
                }}
            >
                <Typography variant="body1">{prefix}</Typography>
                <TextField
                    type="number"
                    value={value}
                    onChange={(e) =>
                        onChange(
                            Math.min(
                                99,
                                Math.max(0, Number(e.target.value) || 0),
                            ),
                        )
                    }
                    size="small"
                    disabled={disabled}
                    inputProps={{
                        min: 0,
                        max: 99,
                        style: { textAlign: 'center', width: 36, padding: 6 },
                    }}
                    style={{ width: 56 }}
                    variant="outlined"
                />
                <Typography variant="body1">{suffix}</Typography>
                <Tooltip title={tooltip}>
                    <InfoOutlined
                        fontSize="small"
                        style={{
                            color: theme.palette.grey[600],
                            cursor: 'help',
                        }}
                    />
                </Tooltip>
            </Container>
        </Box>
    )
}

export const BulkCreateProjectsModal = (props: Props) => {
    const {
        open,
        theme,
        workspaces,
        onClose,
        onScheduleCreated,
        initialYear,
        lockYear,
        hidePropertyPicker,
    } = props
    const queryClient = useQueryClient()

    const [year, setYear] = useState(initialYear ?? currentTurnYear())
    const [startOffsetDays, setStartOffsetDays] = useState(0)
    const [endOffsetDays, setEndOffsetDays] = useState(1)
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
    const [phase, setPhase] = useState<ModalPhase>('form')
    const [progressCurrent, setProgressCurrent] = useState(0)
    const [progressTotal, setProgressTotal] = useState(0)
    const [currentApartmentName, setCurrentApartmentName] = useState<
        string | null
    >(null)
    const [created, setCreated] = useState<CreatedItem[]>([])
    const [skipped, setSkipped] = useState<SkippedItem[]>([])
    const [errors, setErrors] = useState<ErrorItem[]>([])
    const [failureMessage, setFailureMessage] = useState<string | null>(null)

    const pollIntervalRef = useRef<number | null>(null)
    const invalidatedOnCompleteRef = useRef(false)

    const sortedWorkspaces = useMemo(
        () =>
            [...workspaces].sort((a, b) =>
                a.name.localeCompare(b.name, undefined, {
                    sensitivity: 'base',
                }),
            ),
        [workspaces],
    )

    const selectionSeedKeyRef = useRef('')
    const selectionSeedKey = `${open}:${sortedWorkspaces
        .map((w) => w.id)
        .join(',')}`

    const stopPolling = () => {
        if (pollIntervalRef.current !== null) {
            window.clearInterval(pollIntervalRef.current)
            pollIntervalRef.current = null
        }
    }

    const resetJobState = () => {
        stopPolling()
        setPhase('form')
        setProgressCurrent(0)
        setProgressTotal(0)
        setCurrentApartmentName(null)
        setCreated([])
        setSkipped([])
        setErrors([])
        setFailureMessage(null)
        invalidatedOnCompleteRef.current = false
    }

    useEffect(() => {
        if (!open) {
            resetJobState()
            return
        }
        if (initialYear != null) {
            setYear(initialYear)
        }
        if (selectionSeedKeyRef.current === selectionSeedKey) {
            return
        }
        selectionSeedKeyRef.current = selectionSeedKey
        setSelectedIds(new Set(sortedWorkspaces.map((w) => w.id)))
    }, [open, selectionSeedKey, sortedWorkspaces, initialYear])

    useEffect(() => stopPolling, [])

    const invalidateAnalytics = async () => {
        await Promise.all([
            queryClient.invalidateQueries({
                queryKey: ['workspace-project-analytics'],
                exact: false,
            }),
            queryClient.invalidateQueries({
                queryKey: ['project-analytics'],
                exact: false,
            }),
            queryClient.invalidateQueries({
                queryKey: ['project-drafts'],
                exact: false,
            }),
        ])
    }

    const toggleRow = (apartmentId: number, checked: boolean) => {
        setSelectedIds((prev) => {
            const next = new Set(prev)
            if (checked) {
                next.add(apartmentId)
            } else {
                next.delete(apartmentId)
            }
            return next
        })
    }

    const toggleAll = (checked: boolean) => {
        if (!checked) {
            setSelectedIds(new Set())
            return
        }
        setSelectedIds(new Set(sortedWorkspaces.map((w) => w.id)))
    }

    const selectedCount = sortedWorkspaces.filter((w) => selectedIds.has(w.id))
        .length

    const applyProgressPayload = (status: TaskStatusResponse) => {
        if (status.current !== undefined) {
            setProgressCurrent(status.current)
        }
        if (status.total !== undefined) {
            setProgressTotal(status.total)
        }
        if (status.apartment_name !== undefined) {
            setCurrentApartmentName(status.apartment_name)
        }
        if (status.created) {
            setCreated(status.created)
        }
        if (status.skipped) {
            setSkipped(status.skipped)
        }
        if (status.errors) {
            setErrors(status.errors)
        }
    }

    const handleCreate = async () => {
        if (selectedCount === 0) {
            toast.error('Select at least one property')
            return
        }

        const apartmentIds = sortedWorkspaces
            .filter((w) => selectedIds.has(w.id))
            .map((w) => w.id)

        setPhase('running')
        setProgressCurrent(0)
        setProgressTotal(apartmentIds.length)
        setCurrentApartmentName(null)
        setCreated([])
        setSkipped([])
        setErrors([])
        setFailureMessage(null)
        invalidatedOnCompleteRef.current = false

        try {
            const response = await axiosInstance.post<StartTaskResponse>(
                '/workorder/org-project-analytics/bulk-create-term-projects/',
                {
                    year,
                    start_offset_days: startOffsetDays,
                    end_offset_days: endOffsetDays,
                    apartment_ids: apartmentIds,
                },
            )
            const { task_id, total } = response.data
            setProgressTotal(total)

            pollIntervalRef.current = window.setInterval(async () => {
                try {
                    const statusRes = await axiosInstance.get<TaskStatusResponse>(
                        '/workorder/org-project-analytics/bulk-create-term-projects/status/',
                        { params: { task_id } },
                    )
                    const status = statusRes.data
                    applyProgressPayload(status)

                    if (status.state === 'SUCCESS') {
                        stopPolling()
                        const result = status.result
                        setCreated(result?.created ?? status.created ?? [])
                        setSkipped(result?.skipped ?? status.skipped ?? [])
                        setErrors(result?.errors ?? status.errors ?? [])
                        setPhase('done')
                        if (!invalidatedOnCompleteRef.current) {
                            invalidatedOnCompleteRef.current = true
                            void invalidateAnalytics()
                        }
                    } else if (status.state === 'FAILURE') {
                        stopPolling()
                        setFailureMessage(status.error ?? 'Bulk create failed')
                        setPhase('failed')
                    }
                } catch {
                    // Keep polling — a transient network blip should not
                    // mark a still-running Celery job as failed.
                }
            }, POLL_INTERVAL_MS)
        } catch {
            setFailureMessage('Failed to start bulk create')
            setPhase('failed')
        }
    }

    const thisYear = currentTurnYear()
    const yearOptions = [thisYear, thisYear + 1]
    const progressPercent =
        progressTotal > 0
            ? Math.min(100, Math.round((progressCurrent / progressTotal) * 100))
            : 0
    const showProgressView = phase !== 'form'

    const handleClose = () => {
        stopPolling()
        onClose()
    }

    return (
        <Modal
            open={open}
            onClose={handleClose}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
            closeAfterTransition
        >
            <Slide direction="up" in={open}>
                <Paper
                    style={{
                        width: `min(${
                            hidePropertyPicker ? 960 : 720
                        }px, calc(100vw - 64px))`,
                        height: 'calc(100vh - 64px)',
                        maxHeight: 'calc(100vh - 64px)',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}
                >
                    <Container
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: theme.spacing(1.5, 2),
                            borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                        }}
                    >
                        <Typography variant="h6">
                            Bulk Create Projects
                        </Typography>
                        <IconButton onClick={handleClose} size="small">
                            <Close />
                        </IconButton>
                    </Container>

                    {!showProgressView ? (
                        <>
                            <Box
                                style={{
                                    padding: theme.spacing(2),
                                    borderBottom:
                                        '1px solid rgba(0, 0, 0, 0.08)',
                                }}
                            >
                                {!lockYear && (
                                    <>
                                        <Typography
                                            variant="subtitle1"
                                            style={{
                                                fontWeight: 700,
                                                marginBottom: theme.spacing(
                                                    0.75,
                                                ),
                                            }}
                                        >
                                            Turn Year
                                        </Typography>
                                        <TextField
                                            select
                                            value={year}
                                            onChange={(e) =>
                                                setYear(Number(e.target.value))
                                            }
                                            size="small"
                                            style={{ minWidth: 120 }}
                                            variant="outlined"
                                        >
                                            {yearOptions.map((y) => (
                                                <MenuItem key={y} value={y}>
                                                    {y}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </>
                                )}
                                {lockYear && (
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        style={{
                                            marginBottom: theme.spacing(1.5),
                                        }}
                                    >
                                        Turn year {year} · Creating for{' '}
                                        {sortedWorkspaces.length} propert
                                        {sortedWorkspaces.length === 1
                                            ? 'y'
                                            : 'ies'}
                                    </Typography>
                                )}

                                <Box
                                    style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: theme.spacing(2),
                                        marginTop: lockYear
                                            ? 0
                                            : theme.spacing(2),
                                    }}
                                >
                                    <Box>
                                        <Typography
                                            variant="subtitle1"
                                            style={{
                                                fontWeight: 700,
                                                marginBottom: theme.spacing(
                                                    0.75,
                                                ),
                                            }}
                                        >
                                            Name
                                        </Typography>
                                        <TextField
                                            value={`{Property} Turn ${year}`}
                                            size="small"
                                            variant="outlined"
                                            InputProps={{ readOnly: true }}
                                            disabled
                                            style={{ width: 220 }}
                                        />
                                    </Box>
                                    <Box>
                                        <Typography
                                            variant="subtitle1"
                                            style={{
                                                fontWeight: 700,
                                                marginBottom: theme.spacing(
                                                    0.75,
                                                ),
                                            }}
                                        >
                                            ID
                                        </Typography>
                                        <TextField
                                            value={buildScheduleId(year)}
                                            size="small"
                                            variant="outlined"
                                            InputProps={{ readOnly: true }}
                                            disabled
                                            style={{ width: 160 }}
                                        />
                                    </Box>
                                </Box>

                                <Box
                                    style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: theme.spacing(3),
                                        marginTop: theme.spacing(1),
                                    }}
                                >
                                    <Box style={{ flex: '1 1 280px' }}>
                                        <BufferRow
                                            theme={theme}
                                            title="Project Start Date"
                                            prefix="Start project"
                                            value={startOffsetDays}
                                            onChange={setStartOffsetDays}
                                            suffix="days after move-out"
                                            tooltip="Move-out is determined by the mode end date of outgoing leases for this property. Dates are calculated when you create."
                                        />
                                    </Box>
                                    <Box style={{ flex: '1 1 280px' }}>
                                        <BufferRow
                                            theme={theme}
                                            title="Project End Date"
                                            prefix="End project"
                                            value={endOffsetDays}
                                            onChange={setEndOffsetDays}
                                            suffix="days before move-in"
                                            tooltip="Move-in is the earliest strong incoming lease start cohort at least 10 days after move-out. Dates are calculated when you create."
                                        />
                                    </Box>
                                </Box>
                            </Box>

                            {!hidePropertyPicker && (
                                <Box
                                    style={{
                                        padding: theme.spacing(1, 2),
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                    }}
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                color="primary"
                                                checked={
                                                    sortedWorkspaces.length >
                                                        0 &&
                                                    selectedCount ===
                                                        sortedWorkspaces.length
                                                }
                                                indeterminate={
                                                    selectedCount > 0 &&
                                                    selectedCount <
                                                        sortedWorkspaces.length
                                                }
                                                onChange={(e) =>
                                                    toggleAll(e.target.checked)
                                                }
                                            />
                                        }
                                        label={`Select all (${sortedWorkspaces.length})`}
                                    />
                                </Box>
                            )}

                            <Box
                                style={{
                                    overflow: 'auto',
                                    flex: 1,
                                    minHeight: hidePropertyPicker ? 160 : 280,
                                }}
                            >
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            {!hidePropertyPicker && (
                                                <TableCell padding="checkbox" />
                                            )}
                                            <TableCell>Property</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {sortedWorkspaces.map((workspace) => (
                                            <TableRow
                                                key={workspace.id}
                                                hover={!hidePropertyPicker}
                                                selected={
                                                    !hidePropertyPicker &&
                                                    selectedIds.has(
                                                        workspace.id,
                                                    )
                                                }
                                            >
                                                {!hidePropertyPicker && (
                                                    <TableCell padding="checkbox">
                                                        <Checkbox
                                                            color="primary"
                                                            checked={selectedIds.has(
                                                                workspace.id,
                                                            )}
                                                            onChange={(e) =>
                                                                toggleRow(
                                                                    workspace.id,
                                                                    e.target
                                                                        .checked,
                                                                )
                                                            }
                                                        />
                                                    </TableCell>
                                                )}
                                                <TableCell>
                                                    {workspace.name}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {sortedWorkspaces.length === 0 && (
                                            <TableRow>
                                                <TableCell
                                                    colSpan={
                                                        hidePropertyPicker
                                                            ? 1
                                                            : 2
                                                    }
                                                >
                                                    No apartments found for this
                                                    organization.
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </Box>

                            <Container
                                style={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    gap: theme.spacing(1),
                                    padding: theme.spacing(1.5, 2),
                                    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                                }}
                            >
                                <Button onClick={handleClose}>Cancel</Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleCreate}
                                    disabled={selectedCount === 0}
                                >
                                    {`Create ${selectedCount} Draft${
                                        selectedCount === 1 ? '' : 's'
                                    }`}
                                </Button>
                            </Container>
                        </>
                    ) : (
                        <>
                            <Box
                                style={{
                                    padding: theme.spacing(2),
                                    borderBottom:
                                        '1px solid rgba(0, 0, 0, 0.08)',
                                }}
                            >
                                {phase === 'failed' ? (
                                    <Typography color="error">
                                        {failureMessage ?? 'Bulk create failed'}
                                    </Typography>
                                ) : (
                                    <>
                                        <Typography
                                            variant="subtitle1"
                                            style={{ fontWeight: 700 }}
                                        >
                                            {phase === 'done'
                                                ? `Created ${
                                                      created.length
                                                  } draft${
                                                      created.length === 1
                                                          ? ''
                                                          : 's'
                                                  }`
                                                : `Creating draft ${Math.min(
                                                      progressCurrent +
                                                          (phase === 'running'
                                                              ? 1
                                                              : 0),
                                                      progressTotal ||
                                                          selectedCount,
                                                  )} of ${
                                                      progressTotal ||
                                                      selectedCount
                                                  }`}
                                        </Typography>
                                        {phase === 'running' &&
                                            currentApartmentName && (
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                    style={{
                                                        marginTop: theme.spacing(
                                                            0.5,
                                                        ),
                                                    }}
                                                >
                                                    {currentApartmentName}
                                                </Typography>
                                            )}
                                        {phase === 'done' && (
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                                style={{
                                                    marginTop: theme.spacing(
                                                        0.5,
                                                    ),
                                                }}
                                            >
                                                {skipped.length > 0
                                                    ? `Skipped ${skipped.length}`
                                                    : 'All selected properties processed'}
                                                {errors.length > 0
                                                    ? ` · ${
                                                          errors.length
                                                      } error${
                                                          errors.length === 1
                                                              ? ''
                                                              : 's'
                                                      }`
                                                    : ''}
                                            </Typography>
                                        )}
                                        <Box
                                            style={{
                                                marginTop: theme.spacing(1.5),
                                            }}
                                        >
                                            <LinearProgress
                                                variant="determinate"
                                                value={
                                                    phase === 'done'
                                                        ? 100
                                                        : progressPercent
                                                }
                                            />
                                        </Box>
                                    </>
                                )}
                            </Box>

                            <Box
                                style={{
                                    flex: 1,
                                    minHeight: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: theme.spacing(1.5),
                                    padding: theme.spacing(2),
                                    overflow: 'hidden',
                                }}
                            >
                                <Box
                                    style={{
                                        flex: 1,
                                        minHeight: 0,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        border: '1px solid rgba(0, 0, 0, 0.08)',
                                        borderRadius: 4,
                                        overflow: 'hidden',
                                    }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        style={{
                                            fontWeight: 700,
                                            padding: theme.spacing(1, 1.5),
                                            borderBottom:
                                                '1px solid rgba(0, 0, 0, 0.08)',
                                            flexShrink: 0,
                                        }}
                                    >
                                        Created ({created.length})
                                    </Typography>
                                    <Box
                                        style={{
                                            flex: 1,
                                            minHeight: 0,
                                            overflow: 'auto',
                                            padding: theme.spacing(1, 1.5),
                                        }}
                                    >
                                        {created.length === 0 ? (
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                            >
                                                {phase === 'running'
                                                    ? 'Waiting…'
                                                    : 'None'}
                                            </Typography>
                                        ) : (
                                            created.map((item) => (
                                                <Typography
                                                    key={item.apartment_id}
                                                    variant="body2"
                                                    style={{
                                                        marginBottom: theme.spacing(
                                                            0.5,
                                                        ),
                                                    }}
                                                >
                                                    {item.name ??
                                                        item.apartment_name}
                                                </Typography>
                                            ))
                                        )}
                                    </Box>
                                </Box>

                                <Box
                                    style={{
                                        flex: 1,
                                        minHeight: 0,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        border: '1px solid rgba(0, 0, 0, 0.08)',
                                        borderRadius: 4,
                                        overflow: 'hidden',
                                    }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        style={{
                                            fontWeight: 700,
                                            padding: theme.spacing(1, 1.5),
                                            borderBottom:
                                                '1px solid rgba(0, 0, 0, 0.08)',
                                            flexShrink: 0,
                                        }}
                                    >
                                        Skipped ({skipped.length})
                                    </Typography>
                                    <Box
                                        style={{
                                            flex: 1,
                                            minHeight: 0,
                                            overflow: 'auto',
                                            padding: theme.spacing(1, 1.5),
                                        }}
                                    >
                                        {skipped.length === 0 ? (
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                            >
                                                {phase === 'running'
                                                    ? 'Waiting…'
                                                    : 'None'}
                                            </Typography>
                                        ) : (
                                            skipped.map((item) => (
                                                <Typography
                                                    key={item.apartment_id}
                                                    variant="body2"
                                                    style={{
                                                        marginBottom: theme.spacing(
                                                            0.5,
                                                        ),
                                                    }}
                                                >
                                                    {item.apartment_name} —{' '}
                                                    {item.reason}
                                                </Typography>
                                            ))
                                        )}
                                    </Box>
                                </Box>

                                {errors.length > 0 && (
                                    <Box
                                        style={{
                                            flex: '0 1 120px',
                                            minHeight: 0,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            border: `1px solid ${theme.palette.error.light}`,
                                            borderRadius: 4,
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <Typography
                                            variant="subtitle2"
                                            style={{
                                                fontWeight: 700,
                                                padding: theme.spacing(1, 1.5),
                                                borderBottom: `1px solid ${theme.palette.error.light}`,
                                                color: theme.palette.error.main,
                                                flexShrink: 0,
                                            }}
                                        >
                                            Errors ({errors.length})
                                        </Typography>
                                        <Box
                                            style={{
                                                flex: 1,
                                                minHeight: 0,
                                                overflow: 'auto',
                                                padding: theme.spacing(1, 1.5),
                                            }}
                                        >
                                            {errors.map((item) => (
                                                <Typography
                                                    key={item.apartment_id}
                                                    variant="body2"
                                                    color="error"
                                                    style={{
                                                        marginBottom: theme.spacing(
                                                            0.5,
                                                        ),
                                                    }}
                                                >
                                                    {item.apartment_name ??
                                                        `Apartment ${item.apartment_id}`}{' '}
                                                    — {item.error}
                                                </Typography>
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                            </Box>

                            <Container
                                style={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    gap: theme.spacing(1),
                                    padding: theme.spacing(1.5, 2),
                                    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                                }}
                            >
                                {phase === 'done' &&
                                    onScheduleCreated &&
                                    created.length > 0 && (
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            onClick={() => {
                                                const ids = created
                                                    .map((c) => c.schedule_id)
                                                    .filter(
                                                        (id): id is number =>
                                                            id != null,
                                                    )
                                                onScheduleCreated(year, ids)
                                            }}
                                        >
                                            Schedule created projects
                                        </Button>
                                    )}
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleClose}
                                >
                                    {phase === 'running' ? 'Close' : 'Done'}
                                </Button>
                            </Container>
                        </>
                    )}
                </Paper>
            </Slide>
        </Modal>
    )
}
